/**
 * "Anteckningar" inside work-order section 9 (Dokumentation).
 *
 * Pure presentational: props in, callbacks out. No Firebase, no app contexts.
 * Errors from the callbacks are shown inline as an Alert.
 */
import React, { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { MessageSquare, Edit2, Trash2, Save, X } from 'lucide-react';
import { SectionTitle } from './SectionTitle';
import { ProjectNote } from '../../../../types/projects';
import ConfirmationModal from '../../../../Components/ConfirmationModal';
import { WORK_ORDER_LABELS } from '../../workOrderSections';
import './documentation.css';

export interface NotesSectionProps {
    /** Any order; the component sorts newest first. */
    notes: ProjectNote[];
    currentUserId: string;
    /** false → read-only. */
    canEdit: boolean;
    /** Admin: may delete and edit any note; otherwise only own notes. */
    canDeleteAny?: boolean;
    /** Default: the author is not shown. */
    resolveAuthorName?: (uid: string) => string;
    onAdd: (text: string) => Promise<void>;
    onUpdate?: (noteKey: string, text: string) => Promise<void>;
    onDelete: (noteKey: string) => Promise<void>;
}

const MAX_CONTENT_LENGTH = 5000;

function errorMessage(err: unknown, fallback: string): string {
    return err instanceof Error && err.message ? err.message : fallback;
}

function sortNewestFirst(notes: ProjectNote[]): ProjectNote[] {
    return [...notes].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

const NotesSection: React.FC<NotesSectionProps> = ({
    notes,
    currentUserId,
    canEdit,
    canDeleteAny = false,
    resolveAuthorName,
    onAdd,
    onUpdate,
    onDelete,
}) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [newContent, setNewContent] = useState('');
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editingContent, setEditingContent] = useState('');
    const [deleteKey, setDeleteKey] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sorted = sortNewestFirst(notes);
    const canManage = (note: ProjectNote) => canEdit && (canDeleteAny || note.createdBy === currentUserId);

    const run = async (action: () => Promise<void>, fallback: string): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await action();
            return true;
        } catch (err) {
            setError(errorMessage(err, fallback));
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const handleAdd = async () => {
        const text = newContent.trim();
        if (!text) return;
        if (await run(() => onAdd(text), 'Kunde inte spara anteckningen')) {
            setNewContent('');
            setShowAddForm(false);
        }
    };

    const handleSaveEdit = async () => {
        const text = editingContent.trim();
        if (!editingKey || !text || !onUpdate) return;
        const key = editingKey;
        if (await run(() => onUpdate(key, text), 'Kunde inte spara anteckningen')) {
            setEditingKey(null);
            setEditingContent('');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteKey) return;
        const key = deleteKey;
        await run(() => onDelete(key), 'Kunde inte ta bort anteckningen');
        setDeleteKey(null);
    };

    return (
        <div className="wo-notes">
            <SectionTitle
                icon={MessageSquare}
                title={WORK_ORDER_LABELS.notes}
                count={sorted.length}
                level="sub"
                onAdd={canEdit && !showAddForm ? () => setShowAddForm(true) : undefined}
                addLabel={WORK_ORDER_LABELS.add}
                adding={submitting}
            />

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-2">
                    {error}
                </Alert>
            )}

            {canEdit && showAddForm && (
                <div className="wo-notes__form mb-3 p-3 bg-light rounded">
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Skriv din anteckning..."
                        aria-label="Ny anteckning"
                        value={newContent}
                        onChange={(e) => setNewContent(e.target.value)}
                        maxLength={MAX_CONTENT_LENGTH}
                        disabled={submitting}
                        autoFocus
                    />
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className={newContent.length >= MAX_CONTENT_LENGTH ? 'text-danger' : 'text-muted'}>
                            {newContent.length}/{MAX_CONTENT_LENGTH}
                        </small>
                        <div className="d-flex gap-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => { setShowAddForm(false); setNewContent(''); }}
                                disabled={submitting}
                            >
                                Avbryt
                            </Button>
                            <Button variant="primary" size="sm" onClick={handleAdd} disabled={!newContent.trim() || submitting}>
                                {submitting ? 'Sparar...' : 'Spara'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {sorted.length === 0 && !showAddForm && (
                <p className="text-muted mb-0">Inga anteckningar ännu</p>
            )}

            <div className="wo-notes__list">
                {sorted.map((note) => (
                    <div key={note.key} data-testid={`wo-note-${note.key}`} className="wo-notes__note p-3 border rounded mb-2">
                        {editingKey === note.key ? (
                            <>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    aria-label="Ändra anteckning"
                                    value={editingContent}
                                    onChange={(e) => setEditingContent(e.target.value)}
                                    maxLength={MAX_CONTENT_LENGTH}
                                    disabled={submitting}
                                    autoFocus
                                />
                                <small className={`d-block text-end mt-1 ${editingContent.length >= MAX_CONTENT_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                    {editingContent.length}/{MAX_CONTENT_LENGTH}
                                </small>
                                <div className="d-flex justify-content-end gap-2 mt-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => { setEditingKey(null); setEditingContent(''); }}
                                        disabled={submitting}
                                    >
                                        <X size={14} className="me-1" />
                                        Avbryt
                                    </Button>
                                    <Button variant="primary" size="sm" onClick={handleSaveEdit} disabled={!editingContent.trim() || submitting}>
                                        <Save size={14} className="me-1" />
                                        {submitting ? 'Sparar...' : 'Spara'}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                {note.title && <h6 className="mb-2">{note.title}</h6>}
                                <p className="mb-2 wo-notes__content">{note.content}</p>
                                <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                                    <small className="text-muted">
                                        {note.createdAt?.substring(0, 10)}
                                        {resolveAuthorName && note.createdBy && ` · ${resolveAuthorName(note.createdBy)}`}
                                    </small>
                                    {canManage(note) && (
                                        <div className="wo-notes__actions d-flex gap-2">
                                            {onUpdate && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    aria-label={`${WORK_ORDER_LABELS.edit} anteckning`}
                                                    onClick={() => { setEditingKey(note.key); setEditingContent(note.content || ''); }}
                                                    disabled={submitting}
                                                >
                                                    <Edit2 size={14} />
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                aria-label="Ta bort anteckning"
                                                onClick={() => setDeleteKey(note.key)}
                                                disabled={submitting}
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            <ConfirmationModal
                show={deleteKey !== null}
                title="Ta bort anteckning"
                message="Vill du ta bort denna anteckning?"
                variant="danger"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteKey(null)}
                loading={submitting}
            />
        </div>
    );
};

export default NotesSection;
